import { useCallback, useEffect, useState } from "react";
import { fetchNotificacoesAtivas, type AvisoSistema as Aviso } from "../integration";
import { IconAlert, IconWrench, IconClipboardCheck, IconClose } from "../icons";

const DISMISSED_KEY = "sercamp-avisos-dispensados";
const POLL_MS = 60_000;

/** Lê a lista de ids de avisos já dispensados pelo usuário neste navegador. */
function readDismissed(): number[] {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function addDismissed(id: number) {
  const atual = readDismissed();
  if (!atual.includes(id)) {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...atual, id]));
  }
}

const TIPO_INFO: Record<Aviso["tipo"], { label: string; Icon: typeof IconAlert }> = {
  falha: { label: "Falha", Icon: IconAlert },
  manutencao: { label: "Manutenção", Icon: IconWrench },
  comunicado: { label: "Comunicado", Icon: IconClipboardCheck },
};

/**
 * Faixa de avisos do Admin Cybersecurity (Portal), exibida no topo do app
 * já autenticado. Busca ao montar e a cada 60s; endpoint público,
 * somente leitura. Dispensar um aviso o remove e guarda o id no
 * localStorage para não reaparecer nas próximas checagens.
 */
export default function AvisoSistema() {
  const [avisos, setAvisos] = useState<Aviso[]>([]);

  const carregar = useCallback(async () => {
    try {
      const lista = await fetchNotificacoesAtivas();
      const dispensados = readDismissed();
      setAvisos(lista.filter((a) => !dispensados.includes(a.id)));
    } catch {
      // Falha ao falar com o Portal: não exibe nada, não incomoda o usuário.
    }
  }, []);

  useEffect(() => {
    carregar();
    const t = setInterval(carregar, POLL_MS);
    return () => clearInterval(t);
  }, [carregar]);

  function dispensar(id: number) {
    addDismissed(id);
    setAvisos((prev) => prev.filter((a) => a.id !== id));
  }

  if (avisos.length === 0) return null;

  return (
    <div className="aviso-stack">
      {avisos.map((a) => {
        const info = TIPO_INFO[a.tipo] ?? TIPO_INFO.comunicado;
        const Icon = info.Icon;
        return (
          <div key={a.id} className={`aviso-bar aviso-${a.tipo}`}>
            <span className="aviso-icon">
              <Icon size={17} />
            </span>
            <span className="aviso-texto">
              <strong>{a.titulo}</strong> — {a.mensagem}
            </span>
            <button
              className="aviso-fechar"
              onClick={() => dispensar(a.id)}
              title="Dispensar aviso"
              aria-label="Dispensar aviso"
            >
              <IconClose size={15} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
